import { ActionIcon, ThemeIcon } from "@mantine/core";
import IconAdjustmentsHorizontal from "@src/assets/icons/tabler/adjustments-horizontal.svg?react";
import IconCaretDownFilled from "@src/assets/icons/tabler/caret-down.svg?react";
import IconCaretRightFilled from "@src/assets/icons/tabler/caret-right.svg?react";
import IconFolderPlus from "@src/assets/icons/tabler/folder-plus.svg?react";
import IconUsers from "@src/assets/icons/tabler/users.svg?react";
import {
	fetchNavbarBranchQueryOptions,
	findBranchKeysContainingInSection,
} from "@src/components/board/queries/fetch-ranklist";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import { NavbarSection } from "@src/gql/graphql";
import type { BranchData } from "@src/lib/clean-tree";
import { Tree } from "@src/lib/clean-tree";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";
import { useNavPendingRename } from "../context/nav-pending-rename-context";
import { useSearch } from "../context/search-context";
import { NavSectionLoadingItem } from "../nav-section-panel/nav-section-loading-item";
import NavSectionTextLabel from "../nav-section-panel/nav-section-text-label";
import { ManageBoardsModal } from "./manage-boards-modal";
import { MoveToFolderModal } from "./move-to-folder-modal";
import { createNavTreeItemRenderer } from "./nav-tree-item";
import { TREE_INDENT_PER_LEVEL_PX, readTreeOpenState } from "./tree-indent";
import { useNavBranchItems, useRanklistTree } from "./use-ranklist-tree";

const OPEN_STATE_KEY = "nav-shared-tree-open-state";

const SHARED_SECTION = NavbarSection.SharedWithMe;

function useSharedBranchItems(branchId: string | null): BranchData {
	return useNavBranchItems(branchId, SHARED_SECTION);
}

export function SharedTreePanel({
	hideSectionHeader = false,
}: {
	hideSectionHeader?: boolean;
} = {}) {
	const queryClient = useQueryClient();
	const {
		handleMoveItem,
		handleCreateFolder,
		handleDeleteFolder,
		isCreatingFolder,
	} = useRanklistTree(SHARED_SECTION);
	const { pendingRenameItemId, requestRename, consumeRename } =
		useNavPendingRename();
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [initialOpenState] = useState(() => readTreeOpenState(OPEN_STATE_KEY));

	const { items: rootItems, isLoading } = useSharedBranchItems(null);
	const { query: navSearchQuery } = useSearch();
	const treeDragAndDropEnabled = !navSearchQuery.trim();

	const { data: favouriteBranch } = useQuery(
		fetchNavbarBranchQueryOptions(NavbarSection.Favourites, null),
	);
	const favouriteKeys = useMemo(
		() => new Set((favouriteBranch ?? []).map((item) => item.key)),
		[favouriteBranch],
	);

	const [moveModalItemId, setMoveModalItemId] = useState<string | null>(null);
	const [moveModalSourceParent, setMoveModalSourceParent] = useState<
		string | null
	>(null);
	const [manageBoardsOpen, setManageBoardsOpen] = useState(false);

	const handleMoveToFolder = useCallback(
		(itemId: string) => {
			const branchKeys = findBranchKeysContainingInSection(
				queryClient,
				itemId,
				SHARED_SECTION,
			);
			const rawParentKey = branchKeys[0]?.[2] as string | undefined;
			const sourceParent =
				rawParentKey === "root" || rawParentKey == null ? null : rawParentKey;
			setMoveModalSourceParent(sourceParent);
			setMoveModalItemId(itemId);
		},
		[queryClient],
	);

	const handleDeleteFolderFromMenu = useCallback(
		(folderId: string) => {
			handleDeleteFolder(folderId, null);
		},
		[handleDeleteFolder],
	);

	const renderNavTreeItem = useMemo(
		() =>
			createNavTreeItemRenderer({
				favouriteKeys,
				onDeleteFolder: handleDeleteFolderFromMenu,
				onMoveToFolder: handleMoveToFolder,
				activeSource: "shared-with-me",
				navbarSection: SHARED_SECTION,
				showMoveToFolder: true,
				pendingRenameItemId,
				onPendingRenameHandled: consumeRename,
			}),
		[
			favouriteKeys,
			handleDeleteFolderFromMenu,
			handleMoveToFolder,
			pendingRenameItemId,
			consumeRename,
		],
	);

	if (isLoading) {
		return <NavSectionLoadingItem />;
	}

	if (rootItems.length === 0) {
		if (hideSectionHeader) {
			return (
				<div className="flex flex-col items-center justify-center py-10 text-center text-text-muted px-4">
					<IconUsers className="w-8 h-8 mb-2 opacity-30" />
					<p className="text-xs font-medium">No shared items yet</p>
					<p className="text-[11px] text-text-secondary mt-1">
						Items shared with you by others will appear here.
					</p>
				</div>
			);
		}
		return null;
	}

	return (
		<div className={clsx("rounded-lg w-0 min-w-full pr-1 pl-0")}>
			{hideSectionHeader ? null : (
				<div
					className={clsx(
						"inline-flex items-center justify-center max-w-full",
						"min-w-full cursor-pointer whitespace-nowrap gap-0.5 h-8 bg-surface-default",
						"sticky top-0 z-10",
					)}
					onClick={() => setIsCollapsed((v) => !v)}
				>
					<ThemeIcon
						variant="transparent"
						size="xs"
						color="var(--ui-color-text-primary)"
					>
						{!isCollapsed ? <IconCaretDownFilled /> : <IconCaretRightFilled />}
					</ThemeIcon>

					<NavSectionTextLabel
						className="grow pointer-events-auto select-none max-w-full ml-2 font-bold text-sm sm:text-xs text-text-secondary uppercase"
						label="Shared With Me"
						size="sm"
					/>

					<Tooltip
						label="Manage shared workspace"
						openDelay={200}
						zIndex={9999}
						withArrow
					>
						<ActionIcon
							variant="subtle"
							size="md"
							color="var(--ui-color-text-muted)"
							aria-label="Manage shared workspace"
							title="Manage shared workspace"
							onClick={(e) => {
								e.stopPropagation();
								setManageBoardsOpen(true);
							}}
						>
							<IconAdjustmentsHorizontal width={16} height={16} />
						</ActionIcon>
					</Tooltip>
					<Tooltip label="New Folder" openDelay={200} zIndex={9999} withArrow>
						<ActionIcon
							variant="subtle"
							size="md"
							color="var(--ui-color-text-muted)"
							aria-label="New folder"
							title="New folder"
							disabled={isCreatingFolder}
							onClick={async (e) => {
								e.stopPropagation();
								const createdFolderId = await handleCreateFolder(null, {
									id: nanoid(),
									isFolder: true,
									name: "New Folder",
								});

								if (createdFolderId) {
									requestRename(createdFolderId);
								}
							}}
						>
							<IconFolderPlus width={16} height={16} />
						</ActionIcon>
					</Tooltip>
				</div>
			)}

			{(hideSectionHeader || !isCollapsed) && rootItems.length > 0 && (
				<Tree
					useBranchItems={useSharedBranchItems}
					renderItem={renderNavTreeItem}
					indentPerLevel={TREE_INDENT_PER_LEVEL_PX}
					dragAndDropEnabled={treeDragAndDropEnabled}
					initialOpenState={initialOpenState}
					onOpenStateChange={(id, isOpen) => {
						const state = readTreeOpenState(OPEN_STATE_KEY);
						state[id] = isOpen;
						localStorage.setItem(OPEN_STATE_KEY, JSON.stringify(state));
					}}
					onMoveItem={handleMoveItem}
					onCreateFolder={handleCreateFolder}
					onDeleteFolder={handleDeleteFolder}
					renderLoading={() => <NavSectionLoadingItem />}
				/>
			)}

			<MoveToFolderModal
				opened={moveModalItemId !== null}
				onClose={() => setMoveModalItemId(null)}
				itemKey={moveModalItemId ?? ""}
				sourceParentKey={moveModalSourceParent}
				section={SHARED_SECTION}
			/>

			<ManageBoardsModal
				opened={manageBoardsOpen}
				onClose={() => setManageBoardsOpen(false)}
				section={SHARED_SECTION}
			/>
		</div>
	);
}
